import React, { useEffect, useState } from 'react';
import safeToast from '../../utils/safeToast';
import { listEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate, renderEmailTemplate, testSendEmailTemplate, listEmailTemplatePlaceholders, type EmailTemplateItem } from '../../api/emailTemplates';

// Lightweight admin panel for managing outbound email templates
export default function EmailTemplatesPanel() {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<EmailTemplateItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [placeholdersMap, setPlaceholdersMap] = useState<Record<string,string[]>>({});
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<{ key?: string; category?: string; subjectTemplate?: string; htmlTemplate?: string; textTemplate?: string; placeholders?: string; disabled?: boolean }>({ key: '', subjectTemplate: '', htmlTemplate: '', textTemplate: '', placeholders: '' });
  const [previewDataRaw, setPreviewDataRaw] = useState<string>('{}');
  const [preview, setPreview] = useState<{ subject?: string; text?: string; html?: string } | null>(null);
  const [testTo, setTestTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await listEmailTemplates();
      setTemplates(res.items);
      const ph = await listEmailTemplatePlaceholders().catch(()=>({}));
      setPlaceholdersMap(ph);
    } catch (e:any) {
      safeToast('error', e?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const active = templates.find(t => t.id === activeId) || null;

  const handleSelect = (id: string) => {
    setActiveId(id);
    const t = templates.find(x => x.id === id);
    if (t) {
      setForm({
        key: t.key,
        category: t.category,
        subjectTemplate: t.subjectTemplate || '',
        htmlTemplate: t.htmlTemplate || '',
        textTemplate: t.textTemplate || '',
        placeholders: (t.placeholders || []).join(', '),
        disabled: !!t.disabled,
      });
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      const payload = {
        key: form.key?.trim() || undefined, // allow server to auto-generate when omitted
        category: form.category?.trim() || undefined,
        subjectTemplate: form.subjectTemplate?.trim() || undefined,
        htmlTemplate: form.htmlTemplate?.trim() || undefined,
        textTemplate: form.textTemplate?.trim() || undefined,
        placeholders: form.placeholders ? form.placeholders.split(',').map(s=>s.trim()).filter(Boolean) : [],
        disabled: form.disabled || undefined,
      };
      const created = await createEmailTemplate(payload);
      safeToast('success',`Template created (key: ${created.key})`);
      setForm({ key: '', subjectTemplate: '', htmlTemplate: '', textTemplate: '', placeholders: '' });
      await load();
    } catch (e:any) {
      safeToast('error', e?.message || 'Create failed');
    } finally { setCreating(false); }
  };

  const handleUpdate = async () => {
    if (!active) return;
    try {
      const updates: any = {
        subjectTemplate: form.subjectTemplate,
        htmlTemplate: form.htmlTemplate,
        textTemplate: form.textTemplate,
        category: form.category,
        placeholders: form.placeholders ? form.placeholders.split(',').map(s=>s.trim()).filter(Boolean) : [],
        disabled: !!form.disabled,
      };
      await updateEmailTemplate(active.id, updates);
      safeToast('success','Updated');
      await load();
    } catch (e:any) { safeToast('error', e?.message || 'Update failed'); }
  };

  const handleDelete = async () => {
    if (!active) return;
    if (!window.confirm('Delete template?')) return;
    try {
      await deleteEmailTemplate(active.id);
      safeToast('success','Deleted');
      setActiveId(null);
      await load();
    } catch (e:any) { safeToast('error', e?.message || 'Delete failed'); }
  };

  const handlePreview = async () => {
    if (!active) { safeToast('info','Select a template first'); return; }
    let data: Record<string, any> = {};
    try { data = JSON.parse(previewDataRaw || '{}'); } catch {}
    try {
      const rendered = await renderEmailTemplate(active.id, data, 'json');
      setPreview(rendered as any);
      safeToast('success','Preview updated');
    } catch (e:any) { safeToast('error', e?.message || 'Preview failed'); }
  };

  const handleTestSend = async () => {
    if (!active) { safeToast('info','Select a template'); return; }
    if (!testTo.trim()) { safeToast('error','Recipient email required'); return; }
    let data: Record<string, any> = {};
    try { data = JSON.parse(previewDataRaw || '{}'); } catch {}
    try {
      setSendingTest(true);
      const res = await testSendEmailTemplate(active.id, testTo.trim(), data);
      if (res.skipped) safeToast('info','Skipped (recipient unsubscribed)');
      else safeToast('success',`Sent via ${res.provider || 'unknown'}`);
    } catch (e:any) { safeToast('error', e?.message || 'Test send failed'); }
    finally { setSendingTest(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Email Templates</h2>
        <button onClick={load} className="px-3 py-1 border rounded text-sm">Refresh</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <div className="bg-gray-50 p-3 rounded border">
            {loading && <div className="text-sm text-gray-500">Loading…</div>}
            {!loading && templates.length === 0 && <div className="text-sm text-gray-500">No templates</div>}
            <ul className="space-y-1">
              {templates.map(t => (
                <li key={t.id}>
                  <button onClick={() => handleSelect(t.id)} className={`w-full text-left px-2 py-1 rounded text-sm ${activeId===t.id ? 'bg-blue-600 text-white' : 'hover:bg-white bg-gray-100'}`}>{t.key} {t.disabled ? <span className="text-xs text-red-600">(disabled)</span> : ''}</button>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-4 bg-white p-3 rounded border">
            <h3 className="text-sm font-semibold mb-2">Create New</h3>
            <input value={form.key} onChange={e=> setForm(f=>({ ...f, key: e.target.value }))} placeholder="key (leave blank for auto)" className="w-full mb-2 px-2 py-1 border rounded text-sm" />
            <input value={form.category||''} onChange={e=> setForm(f=>({ ...f, category: e.target.value }))} placeholder="category (optional)" className="w-full mb-2 px-2 py-1 border rounded text-sm" />
            <textarea value={form.subjectTemplate} onChange={e=> setForm(f=>({ ...f, subjectTemplate: e.target.value }))} placeholder="Subject template" className="w-full mb-2 px-2 py-1 border rounded text-sm h-12" />
            <textarea value={form.htmlTemplate} onChange={e=> setForm(f=>({ ...f, htmlTemplate: e.target.value }))} placeholder="HTML template" className="w-full mb-2 px-2 py-1 border rounded text-sm h-20" />
            <textarea value={form.textTemplate} onChange={e=> setForm(f=>({ ...f, textTemplate: e.target.value }))} placeholder="Text template" className="w-full mb-2 px-2 py-1 border rounded text-sm h-20" />
            <input value={form.placeholders||''} onChange={e=> setForm(f=>({ ...f, placeholders: e.target.value }))} placeholder="placeholders (comma-separated)" className="w-full mb-2 px-2 py-1 border rounded text-sm" />
            <label className="flex items-center gap-2 text-xs mb-2"><input type="checkbox" checked={!!form.disabled} onChange={e=> setForm(f=>({ ...f, disabled: e.target.checked }))} /> Disabled</label>
            <button disabled={creating} onClick={handleCreate} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">{creating ? 'Creating…' : 'Create'}</button>
          </div>
        </div>
        <div className="md:col-span-2 space-y-4">
          {active ? (
            <div className="bg-white p-4 rounded border space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Editing: {active.key}</h3>
                <div className="flex gap-2">
                  <button onClick={handleUpdate} className="px-3 py-1 bg-green-600 text-white rounded text-sm">Save</button>
                  <button onClick={handleDelete} className="px-3 py-1 bg-red-600 text-white rounded text-sm">Delete</button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium">Subject</label>
                  <textarea value={form.subjectTemplate} onChange={e=> setForm(f=>({ ...f, subjectTemplate: e.target.value }))} className="w-full px-2 py-1 border rounded text-xs h-16" />
                </div>
                <div>
                  <label className="text-xs font-medium">Placeholders (comma)</label>
                  <input value={form.placeholders||''} onChange={e=> setForm(f=>({ ...f, placeholders: e.target.value }))} className="w-full px-2 py-1 border rounded text-xs" />
                  <div className="text-[10px] text-gray-500 mt-1">Existing: {(placeholdersMap[active.key]||[]).join(', ') || '—'}</div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium">HTML</label>
                  <textarea value={form.htmlTemplate} onChange={e=> setForm(f=>({ ...f, htmlTemplate: e.target.value }))} className="w-full px-2 py-1 border rounded text-xs h-40 font-mono" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium">Text</label>
                  <textarea value={form.textTemplate} onChange={e=> setForm(f=>({ ...f, textTemplate: e.target.value }))} className="w-full px-2 py-1 border rounded text-xs h-28 font-mono" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium">Preview Data (JSON)</label>
                  <textarea value={previewDataRaw} onChange={e=> setPreviewDataRaw(e.target.value)} className="w-full px-2 py-1 border rounded text-xs h-32 font-mono" />
                  <div className="flex gap-2 mt-2">
                    <button onClick={handlePreview} className="px-3 py-1 bg-indigo-600 text-white rounded text-xs">Preview</button>
                    <input value={testTo} onChange={e=> setTestTo(e.target.value)} placeholder="test recipient" className="px-2 py-1 border rounded text-xs" />
                    <button disabled={sendingTest} onClick={handleTestSend} className="px-3 py-1 bg-purple-600 text-white rounded text-xs">{sendingTest ? 'Sending…' : 'Test Send'}</button>
                  </div>
                </div>
              </div>
              {preview && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded p-2">
                    <h4 className="text-xs font-semibold mb-1">Rendered Subject</h4>
                    <div className="text-xs bg-gray-50 p-2 rounded break-words">{preview.subject}</div>
                    <h4 className="text-xs font-semibold mt-3 mb-1">Rendered Text</h4>
                    <pre className="text-[10px] bg-gray-50 p-2 rounded whitespace-pre-wrap">{preview.text}</pre>
                  </div>
                  <div className="border rounded p-2">
                    <h4 className="text-xs font-semibold mb-1">Rendered HTML</h4>
                    <div className="bg-gray-50 p-2 rounded" dangerouslySetInnerHTML={{ __html: preview.html || '' }} />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white p-4 rounded border text-sm text-gray-600">Select a template to edit or create a new one.</div>
          )}
        </div>
      </div>
  <div className="text-[10px] text-gray-400">Interpolation syntax: {'{{placeholder}}'}. Branding footer auto-injected in most send flows; you can still reference {'{{brandFooter}}'} explicitly.</div>
    </div>
  );
}
